import { Chip as ChipButton } from "@design-system/Inputs/Chip";
import { FlexRow } from "@design-system/Layout/Flex";
import { ClassValue, cn } from "@design-system/Utilities";
import {
    createContext,
    useContext,
    useEffect,
    type ButtonHTMLAttributes,
    type FC,
    type PropsWithChildren,
    type Ref,
} from "react";

type ChipSelectContext = {
    values: string[];
    onValuesChange: (values: string[]) => void;
    mode: "single" | "multi";
};

export const ChipSelectContext = createContext<ChipSelectContext | null>(null);

type ChipComponentProps = { value: string };

export type ChipComponentType = FC<
    PropsWithChildren<
        ChipComponentProps & ButtonHTMLAttributes<HTMLButtonElement>
    > & {
        ref?: Ref<HTMLButtonElement>;
    }
>;

const Chip: ChipComponentType = ({ children, value, ref }) => {
    const ctx = useContext(ChipSelectContext);
    if (!ctx) throw new Error("Context must not null");
    const { values, onValuesChange, mode } = ctx;
    const selected = values.includes(value);
    const toggleActive = () => {
        if (mode === "single") {
            onValuesChange(selected ? [] : [value]);
            return;
        }
        if (selected) {
            return onValuesChange(values.filter((elt) => elt !== value));
        }

        return onValuesChange([...values, value]);
    };

    return (
        <ChipButton active={selected} onClick={toggleActive} ref={ref}>
            <ChipButton.Value variant={selected ? "body-sm-bold" : "body-sm"}>
                {children}
            </ChipButton.Value>
        </ChipButton>
    );
};

export type ChipSelectComponentType = FC<
    PropsWithChildren<{
        values: string[];
        onValuesChange: (values: string[]) => void;
        className?: ClassValue;
        mode?: "single" | "multi";
    }>
> & {
    Chip: typeof Chip;
};

export const ChipSelect: ChipSelectComponentType = ({
    children,
    values,
    onValuesChange,
    className,
    mode = "multi",
}) => {
    useEffect(() => {
        if (mode === "single" && values.length > 1) {
            onValuesChange([values[values.length - 1]]);
        }
    }, [mode]);

    return (
        <ChipSelectContext.Provider value={{ values, onValuesChange, mode }}>
            <FlexRow alignItems="center" gap="2" className={cn(className)}>
                {children}
            </FlexRow>
        </ChipSelectContext.Provider>
    );
};

ChipSelect.Chip = Chip;
